import { auth$ } from '@creditsights/api-ui-web';
// eslint-disable-next-line import/no-unresolved
import { useIsAuthenticated } from '@creditsights/navbar-web';
import React, { ReactElement, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';

import './Base.scss';
import classnames from 'classnames/bind';
import { Loader, useCopyPasteGuard } from '@creditsights/styleguide-web';
import ToasterContainer from './components/toaster/ToasterContainer';
import UpgradeToPrint from './components/UpgradeToPrint';
import {
  ANALYST_ROUTE,
  ANALYSTS_ROUTE,
  COMPLIANCE_AND_CONSENT_ROUTE,
  COVENANT_COMPARE_DETAILS_ROUTE,
  COVENANT_COMPARE_ROUTE,
  DATA_TOOLS_ROUTE,
  DEALS_PIPELINE_BONDS_ROUTE,
  DEALS_PIPELINE_COMBINED_ROUTE,
  DEALS_PIPELINE_LOANS_ROUTE,
  DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS,
  DISCLAIMER_ROUTE,
  DOC_SCORES_EU_ROUTE,
  DOC_SCORES_US_ROUTE,
  DOCUMENT_SEARCH_ALL_ROUTE,
  DOCUMENT_SEARCH_REPORTS_ROUTE,
  DOCUMENT_SEARCH_ROUTE,
  DOCUMENT_SEARCH_SOURCE_DOCUMENTS_ROUTE,
  LOAN_COMPARE_DETAILS_ROUTE,
  LOAN_COMPARE_ROUTE,
  MARKET_STATS_BONDS_ROUTE,
  MARKET_STATS_LOANS_ROUTE,
  MARKET_STATS_SPECIAL_SITUATIONS_ROUTE,
  MARKET_STATS_STRUCTURED_FINANCE_ROUTE,
  NOT_FOUND,
  SUPPORTED_ROUTES,
  USER_ONBOARDING_ROUTE,
  USER_PROFILE_ROUTE,
} from './Constants';
import useFetchUserPermissions from './hooks/user/useFetchUserPermissions';
import Analyst from './pages/analysts/Analyst';
import Team from './pages/analysts/Team';
import CovenantCompare from './pages/data-tools/covenant-compare/CovenantCompare';
import CovenantCompareDetails from './pages/data-tools/covenant-compare/CovenantCompareDetails';
import DocScore from './pages/data-tools/doc-score/DocScore';
import DocumentSearch from './pages/data-tools/document-search/DocumentSearch';
import LoanCompare from './pages/data-tools/loan-compare/LoanCompare';
import LoanCompareDetails from './pages/data-tools/loan-compare/LoanCompareDetails';
import Securities from './pages/deals-pipeline/Securities';
import Disclaimer from './pages/disclaimer/Disclaimer';
import MarketStats from './pages/market-stats/MarketStats';
import Onboarding from './pages/onboarding/Onboarding';
import ComplianceAndConsent from './pages/user-account/compliance-and-consent/ComplianceAndConsent';
import Profile from './pages/user-account/profile/Profile';
import { login } from './services/user.service';
import GlobalModal from './components/modal/GlobalModal';
import { hasPermissionToCopyPaste, hasPermissionToPrint, hasUIPermission } from './utils/permissions.utils';
import useFetchProfile from './hooks/user/useFetchProfile';
import NoPermissions from './components/NoPermissions/NoPermissions';
import { MarketType } from './models/Market';

function Base(): ReactElement {
  const queryClient = useQueryClient();
  const { isAuthenticated, setIsAuthenticated } = useIsAuthenticated(queryClient);
  const { data: userPermissions, isLoading: isPermissionsLoading, isFetched: isPermissionsFetched } = useFetchUserPermissions();
  const { data: profile, isLoading: isProfileLoading, isFetched: isProfileFetched } = useFetchProfile();

  // eslint-disable-next-line no-restricted-globals
  const isCallbackUrl = () => location.pathname === '/callback';
  useCopyPasteGuard(hasPermissionToCopyPaste(userPermissions));

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    if (isCallbackUrl()) {
      handleCallback();
    } else {
      // eslint-disable-next-line no-restricted-globals
      location.href = `/login?redirect=${location.pathname}${location.search}`;
    }
  }, [isAuthenticated, isCallbackUrl()]);

  const handleCallback = async () => {
    // eslint-disable-next-line no-restricted-globals
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const url = params.get('url_params');
    const resp = await login(code, url);

    if (resp) {
      setIsAuthenticated(!!auth$.value);
      if (url) {
        window.location.href = url;
      }
    } else {
      // eslint-disable-next-line no-alert
      alert('You are not authorized to access CreditSights Platform');
      // eslint-disable-next-line no-restricted-globals
      location.href = `${location.protocol}//${location.hostname}`;
    }
  };

  const salesPersonEmail = profile?.assignedAccountManager?.email || DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS;
  const isProfileActive = isProfileFetched && !isProfileLoading && profile?.active;
  const hasPermissions = isPermissionsFetched && !isPermissionsLoading && hasUIPermission(userPermissions);

  if (isAuthenticated && (isPermissionsLoading || isProfileLoading)) {
    return (
      <div className={classnames('loader-container')}>
        <Loader text={null} />
      </div>
    );
  }

  if (isAuthenticated && (!isProfileActive || !hasPermissions)) {
    return <NoPermissions salesPersonEmail={salesPersonEmail} />;
  }

  const canPrint = hasPermissionToPrint(userPermissions);
  if (isAuthenticated) {
    return (
      <Router>
        {!canPrint && <UpgradeToPrint />}
        <div className={classnames('cs-root-content', { 'no-print': !canPrint })} data-testid="cs-content">
          <ToasterContainer />
          <Routes>
            <Route path="">
              <Route path="/callback" element={<Navigate to="/" />} />
              <Route path="/user" element={<Navigate to="/user/onboarding" />} />
              <Route path={USER_ONBOARDING_ROUTE} element={<Onboarding />} />
              <Route path={USER_PROFILE_ROUTE} element={<Profile />} />
              <Route path={COMPLIANCE_AND_CONSENT_ROUTE} element={<ComplianceAndConsent />} />
              <Route path={ANALYSTS_ROUTE} element={<Team />} />
              <Route path={ANALYST_ROUTE} element={<Analyst />} />
              <Route path={DATA_TOOLS_ROUTE} element={<Navigate to={DOC_SCORES_US_ROUTE} />} />
              <Route path={DOCUMENT_SEARCH_ROUTE('bond')} element={<Navigate to={DOCUMENT_SEARCH_ALL_ROUTE('bond')} />} />
              <Route path={DOCUMENT_SEARCH_ALL_ROUTE('bond')} element={<DocumentSearch docType="bond" reportType="all" />} />
              <Route path={DOCUMENT_SEARCH_REPORTS_ROUTE('bond')} element={<DocumentSearch docType="bond" reportType="reports" />} />
              <Route path={DOCUMENT_SEARCH_SOURCE_DOCUMENTS_ROUTE('bond')} element={<DocumentSearch docType="bond" reportType="source_documents" />} />
              <Route path={DOCUMENT_SEARCH_ROUTE('loan')} element={<Navigate to={DOCUMENT_SEARCH_ALL_ROUTE('loan')} />} />
              <Route path={DOCUMENT_SEARCH_ALL_ROUTE('loan')} element={<DocumentSearch docType="loan" reportType="all" />} />
              <Route path={DOCUMENT_SEARCH_REPORTS_ROUTE('loan')} element={<DocumentSearch docType="loan" reportType="reports" />} />
              <Route path={DOCUMENT_SEARCH_SOURCE_DOCUMENTS_ROUTE('loan')} element={<DocumentSearch docType="loan" reportType="source_documents" />} />
              <Route path={LOAN_COMPARE_ROUTE} element={<LoanCompare />} />
              <Route path={LOAN_COMPARE_DETAILS_ROUTE} element={<LoanCompareDetails />} />
              <Route path={COVENANT_COMPARE_ROUTE} element={<CovenantCompare />} />
              <Route path={COVENANT_COMPARE_DETAILS_ROUTE} element={<CovenantCompareDetails />} />
              <Route path={DOC_SCORES_US_ROUTE} element={<DocScore region="us" />} />
              <Route path={DOC_SCORES_EU_ROUTE} element={<DocScore region="eu" />} />
              <Route path={DEALS_PIPELINE_BONDS_ROUTE} element={<Securities type="bond" />} />
              <Route path={DEALS_PIPELINE_LOANS_ROUTE} element={<Securities type="loan" />} />
              <Route path={DEALS_PIPELINE_COMBINED_ROUTE} element={<Securities type="combined" />} />
              <Route path={MARKET_STATS_LOANS_ROUTE} element={<MarketStats type={MarketType.LOANS} />} />
              <Route path={MARKET_STATS_BONDS_ROUTE} element={<MarketStats type={MarketType.BONDS} />} />
              <Route path={MARKET_STATS_SPECIAL_SITUATIONS_ROUTE} element={<MarketStats type={MarketType.SPECIAL_SITUATIONS} />} />
              <Route path={MARKET_STATS_STRUCTURED_FINANCE_ROUTE} element={<MarketStats type={MarketType.STRUCTURED_FINANCE} />} />
              <Route path={DISCLAIMER_ROUTE} element={<Disclaimer />} />
              {SUPPORTED_ROUTES.map((route, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Route key={index} path={`${route}/*`} element={<Navigate to={NOT_FOUND} replace />} />
              ))}
            </Route>
          </Routes>
        </div>
        <GlobalModal />
      </Router>
    );
  }
  return <span />;
}

export default Base;
